import { useEffect, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Controller, useForm } from 'react-hook-form';
import Divider from '@/components/shared/Divider/Divider';
import { useTranslation } from 'react-i18next';
import { Daum } from '@/@types/Affiliates';
import { format, parseISO } from 'date-fns';
import { useEditInviteAffiliateProgram } from '../hooks/useEditInviteAffiliateProgram';
import { EditInviteAffiliateProgramData } from '@/services/affiliate-program.service';
import CommissionInput from '@/components/shared/CommissionInput/CommissionInput';
import CustomSelect from '@/components/shared/CustomSelect/CustomSelect';
import { Input } from '@/components/shared/Input/Input';
import { currencyOptions, formatCurrency } from '@/lib/select-options';

interface EditAffiliateSheetProps {
  affiliate: Daum;
  isOpen: boolean;
  onClose: () => void;
}

export default function SideQuickEdit({
  affiliate,
  isOpen,
  onClose,
}: EditAffiliateSheetProps) {
  const { t } = useTranslation('affiliates');
  const [formattedPrice, setFormattedPrice] = useState('');

  const { editInviteAffiliateProgram, isPending } =
    useEditInviteAffiliateProgram();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    clearErrors,
    control,
    formState: { errors, isSubmitting, defaultValues },
    reset,
  } = useForm<EditInviteAffiliateProgramData>({
    defaultValues: {
      productInvitationId: affiliate?.id,
      commission_value: affiliate?.invite?.commission_value ?? undefined,
      commission_rule_type:
        affiliate?.invite.commission_rule_type || 'percentage',
      currency: affiliate?.invite.currency || 'BRL',
    },
  });

  const onSubmit = async (data: EditInviteAffiliateProgramData) => {
    await editInviteAffiliateProgram(data);

    await onClose();
  };

  const affiliateId = affiliate?.id;
  const inviteCurrency = affiliate?.invite?.currency;
  const inviteRuleType = affiliate?.invite?.commission_rule_type;
  const inviteCommissionValue = affiliate?.invite?.commission_value;

  const resetCommissionByType = (nextType: string) => {
    clearErrors('commission_value');

    if (nextType === 'cpa') {
      const c = watch('currency') || defaultValues?.currency || 'BRL';
      const cents = 100; // R$ 1,00 (ou equivalente) em centavos
      setValue('commission_value', cents, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      setFormattedPrice(formatCurrency(cents / 100, c));
      return;
    }

    // percentage
    setValue('commission_value', 1, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    setFormattedPrice('');
  };

  useEffect(() => {
    if (isOpen) {
      const currency = inviteCurrency || 'BRL';
      const ruleType = inviteRuleType || 'percentage';
      const commissionValue = inviteCommissionValue ?? 1;

      reset({
        productInvitationId: affiliateId,
        commission_value: commissionValue,
        commission_rule_type: ruleType,
        currency: currency,
      });

      // Inicializar formattedPrice se for tipo CPA
      if (ruleType === 'cpa') {
        setFormattedPrice(formatCurrency(commissionValue / 100, currency));
      } else {
        setFormattedPrice('');
      }
    }
  }, [
    affiliateId,
    inviteCommissionValue,
    inviteCurrency,
    inviteRuleType,
    isOpen,
    reset,
  ]);

  const commissionTypeOptions = [
    { value: 'percentage', label: t('percentage') },
    { value: 'cpa', label: t('fixed') },
  ];

  const handlePriceChange = (e, setFormattedValue, fieldName) => {
    const rawValue = e.target.value.replace(/\D/g, '');
    const valueInCents = parseInt(rawValue || '0', 10);
    setFormattedValue(
      formatCurrency(
        valueInCents / 100,
        watch('currency') || defaultValues?.currency || 'BRL'
      )
    );
    setValue(fieldName, valueInCents);
  };

  useEffect(() => {
    if (
      watch('commission_rule_type') === 'cpa' &&
      watch('currency') &&
      watch('commission_value') !== undefined
    ) {
      setFormattedPrice(
        formatCurrency(
          Number(watch('commission_value') || 0) / 100,
          watch('currency') || defaultValues?.currency || 'BRL'
        )
      );
    }
  }, [
    watch('currency'),
    watch('commission_value'),
    watch('commission_rule_type'),
    defaultValues?.currency,
  ]);

  

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className='md:w-[504px] w-full p-0 overflow-y-auto'>
        <section className='flex flex-col justify-between h-full'>
          <SheetHeader className='p-6'>
            <SheetTitle className='flex gap-2'>{t('edit_title')}</SheetTitle>
          </SheetHeader>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className='flex flex-col flex-1 justify-between overflow-y-auto'
          >
            <section className='flex flex-col gap-4 px-6'>
              <div>
                <div className='flex justify-between'>
                  <span className='text-sm text-muted'>{t('name')}:</span>
                  <span className='text-sm'>{affiliate?.invite.user.name}</span>
                </div>
                <div className='flex justify-between mt-2'>
                  <span className='text-sm text-muted'>{t('email')}:</span>
                  <span className='text-sm'>{affiliate?.email}</span>
                </div>
                <div className='flex justify-between mt-2'>
                  <span className='text-sm text-muted'>{t('join_date')}:</span>
                  <span className='text-sm font-semibold'>
                    {affiliate?.invite?.user?.created_at &&
                      format(
                        parseISO(affiliate?.invite?.user?.created_at),
                        'dd/MM/yy'
                      )}
                  </span>
                </div>
              </div>

              <Divider className='my-6' />

              <div>
                <p className='text-sm font-semibold leading-[24px] mb-4'>
                  {t('details')}
                </p>
                <div className='mb-4 flex items-center gap-2'>
                  <Controller
                    control={control}
                    name='commission_rule_type'
                    render={({ field }) => (
                      <CustomSelect
                        label={t('commission_type')}
                        options={commissionTypeOptions}
                        value={field.value || 'percentage'}
                        placeholder={t('select_commission_type')}
                        onValueChange={(value) => {
                          field.onChange(value);
                          setValue('commission_rule_type', value, {
                            shouldDirty: true,
                            shouldTouch: true,
                            shouldValidate: true,
                          });
                          resetCommissionByType(value);
                        }}
                      />
                    )}
                  />
                </div>
                {watch('commission_rule_type') === 'percentage' && (
                  <div className='w-full sm:w-3/6 mb-4'>
                    <CommissionInput
                      label={t('commission')}
                      helperText={errors.commission_value?.message}
                      minValue={1}
                      maxValue={100}
                      defaultValue={
                        (() => {
                          const v =
                            watch('commission_value') ??
                            (affiliate?.invite as any)?.commission_value;
                          if (v !== undefined && v !== null) return v;
                          return undefined;
                        })()
                      }
                      onChangeCommission={(value) =>
                        setValue(
                          'commission_value',
                          Number.parseFloat(value || '0')
                        )
                      }
                      dataCy='input-affiliate-commission'
                      {...register('commission_value', {
                        required: t('commission_required'),
                        validate: {
                          min: (value) => value >= 1 || t('commission_min'),
                          max: (value) => value <= 100 || t('commission_max'),
                        },
                      })}
                    />
                  </div>
                )}
                {watch('commission_rule_type') === 'cpa' && (
                  <div className='flex items-center gap-2 mb-4'>
                    <CustomSelect
                      value={
                        watch('currency') || defaultValues?.currency || 'BRL'
                      }
                      label={t('common:currency')}
                      placeholder={t('select_commission_type')}
                      onValueChange={(value) => setValue('currency', value)}
                      options={currencyOptions}
                      className='w-[100px]'
                      id='currencySelect'
                    />
                    <Input
                      value={formattedPrice}
                      onChange={(e) => {
                        handlePriceChange(e, setFormattedPrice, 'commission_value');
                        clearErrors('commission_value');
                      }}
                      label={t('commission')}
                      placeholder={t('value_placeholder')}
                      data-cy='input-affiliate-cpa-value'
                    />
                  </div>
                )}
                {errors.commission_value && (
                  <p className='text-red-500 text-sm mt-1'>
                    {errors.commission_value.message}
                  </p>
                )}
              </div>
            </section>

            <section className='flex items-center justify-end p-4 border-t max-sm:pb-12 mt-4'>
              <Button type='button' variant='ghost' onClick={onClose}>
                {t('cancel')}
              </Button>
              <Button type='submit' disabled={isPending || isSubmitting}>
                {isPending || isSubmitting ? t('saving') : t('save_changes')}
              </Button>
            </section>
          </form>
        </section>
      </SheetContent>
    </Sheet>
  );
}
